import { ReactNode, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal } from '@components/modals/confirm-modal/src/confirm-modal';
import { ModalV2 } from '@components/modals/modal-v2';
import { isMobileImplicitDismiss, type ModalCloseSource } from '@components/modals/modal-v2/src/modal-v2.types';

import { ModalTypes } from './modal.type';
import { ModalContext } from './modal-context';
import { ACTION_MODALS } from './utils';

export const ModalProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  const [currentModalType, setCurrentModalType] = useState<ModalTypes>('');
  const [preventExit, setPreventExit] = useState<boolean>(false);

  const toggleModal = useCallback(
    ({ type, isOpen }: { type: ModalTypes; isOpen?: boolean }) => {
      setCurrentModalType(type);
      const shouldOpen = isOpen ?? !isModalOpen;
      setIsModalOpen(shouldOpen);

      // Reset preventExit when explicitly closing modal
      if (!shouldOpen) {
        setPreventExit(false);
      }
    },
    [isModalOpen]
  );

  const handleCloseModal = useCallback(
    (source?: ModalCloseSource) => {
      if (preventExit && !isMobileImplicitDismiss(source)) {
        setIsConfirmModalOpen(true);
      } else {
        toggleModal({ type: '', isOpen: false });
      }
    },
    [preventExit, toggleModal]
  );

  const handleConfirmModal = () => {
    setPreventExit(false);
    setIsConfirmModalOpen(false);
    toggleModal({ type: '', isOpen: false });
  };

  const ModalComponent = useCallback(() => {
    if (!currentModalType) return null;

    return ACTION_MODALS[currentModalType].component();
  }, [currentModalType]);

  const contextValue = useMemo(
    () => ({ currentModalType, toggleModal, setPreventExit, handleCloseModal }),
    [currentModalType, toggleModal, setPreventExit, handleCloseModal]
  );

  return (
    <ModalContext.Provider value={contextValue}>
      <ModalV2 isOpen={isModalOpen} onClose={handleCloseModal} variant='lg'>
        <ModalComponent />
        <ConfirmModal
          isOpen={isConfirmModalOpen}
          title={t('offers:admissionModalConfirmTitle')}
          handleClickConfirm={handleConfirmModal}
          handleClose={() => setIsConfirmModalOpen(false)}
        />
      </ModalV2>
      {children}
    </ModalContext.Provider>
  );
};
